import { CaretDownIcon, TicketIcon, TipJarIcon } from '@phosphor-icons/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import Accordion from '../../../components/Accordion/Accordion'
import { formatMoney } from '../../../helpers/helpers'
import FeaturedTable from '../../FeaturedTable/FeaturedTable'
import Loader from './Loader'

interface StaticticsProps {
  isLoading?: boolean
  isError?: boolean
  totalSingedUp?: number
  totalStats: {
    signedUp: number
    checkedIn: number
    revenue: number
  }
  groupedStats: {
    ticketId: number
    ticketName: string
    totalSignedUp: number
    totalCheckedIn: number
    revenue: number
  }[]
}

type GroupedStat = StaticticsProps['groupedStats'][number]

const Statictics = ({
  isLoading,
  isError,
  totalSingedUp,
  totalStats,
  groupedStats,
}: StaticticsProps) => {
  const { t } = useTranslation(['attendees'])
  const selectedBuilding = useSelector(selectActiveBuilding)
  const advancedTicketingEnabled = selectedBuilding?.eventTicketsAvailable

  const [isOpen, setIsOpen] = useState(false)
  const containerClassName = `attendees-statistics ${
    isOpen ? 'attendees-statistics--with-table-visible' : ''
  }`
  const accordionButtonClassName = `accordion-button ${isOpen ? 'accordion-button--open' : ''}`

  const ticketsHasRevenue = !groupedStats.every((ticket) => !ticket.revenue)

  const columns = useMemo(
    () => [
      {
        accessor: 'ticketName',
        Header: t('attendees:columns.ticketType'),
      },
      {
        accessor: 'totalSignedUp',
        Header: t('attendees:statistics.totalSignedUp'),
      },
      {
        accessor: 'totalCheckedIn',
        Cell: ({ row }: { row: { original: GroupedStat } }) => row.original.totalCheckedIn,
        Header: t('attendees:statistics.totalCheckedInColumn'),
      },
      {
        accessor: 'revenue',
        Cell: ({ row }: { row: { original: GroupedStat } }) => {
          const revenue = row.original.revenue
          return <div>{formatMoney(revenue)}</div>
        },
        Header: t('attendees:statistics.revenue'),
      },
    ],
    [groupedStats, t],
  )

  if (isLoading) {
    return (
      <div className="attendees-statistics">
        <Loader />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="attendees-statistics">
        <p className="attendees_error-message">{t('attendees:errors.statisticsError')}</p>
      </div>
    )
  }

  return (
    <div className={containerClassName}>
      <div className="attendees-statistics__total-section">
        <div className="event-statistic-item">
          <p className="event-statistic-item__header">
            <TicketIcon />
            <span>{t('attendees:statistics.totalSignedUp')}</span>
          </p>
          <span>{totalSingedUp}</span>
        </div>
        <div className="event-statistic-item">
          <p className="event-statistic-item__header">
            <TicketIcon />
            <span>{t('attendees:statistics.totalCheckedInLabel')}</span>
          </p>
          <span>{totalStats.checkedIn}</span>
        </div>
        {ticketsHasRevenue && (
          <div className="event-statistic-item">
            <p className="event-statistic-item__header">
              <TipJarIcon />
              <span>{t('attendees:statistics.revenue')}</span>
            </p>
            <span>{formatMoney(totalStats.revenue)}</span>
          </div>
        )}
        {advancedTicketingEnabled && (
          <div className={accordionButtonClassName} onClick={() => setIsOpen((p) => !p)}>
            <CaretDownIcon />
          </div>
        )}
      </div>
      {advancedTicketingEnabled && (
        <Accordion collapsed={!isOpen}>
          <Accordion.Body collapsed={!isOpen} additionalClass="attendees_table-container">
            <div className="attendees_table attendees_table-statictics">
              <FeaturedTable
                data={groupedStats}
                columns={columns}
                getRowId={({ ticketId }: GroupedStat) => String(ticketId)}
              />
            </div>
          </Accordion.Body>
        </Accordion>
      )}
    </div>
  )
}

export default Statictics
